'use client'

import { IconCreditCard } from '@tabler/icons-react'

import { formatDeliveryExtendedData } from 'modules/delivery'
import Button from 'shared/ui/Button'
import { ButtonSizes, ButtonVariants } from 'shared/ui/Button/Button.enums'
import { formatDate } from 'shared/utils/date'

import { SaleOrder } from '../../../types'
import { isAwaitingPaymentSaleOrder } from '../../../utils/saleOrders'

import styles from './ProfileSaleOrderDetailsTable.module.scss'

interface ProfileSaleOrderDetailsTableProps {
  order: SaleOrder
  isTmsDelivery: boolean
  showPaymentButton?: boolean
  handlePaymentStart: () => void
}

const ProfileSaleOrderDetailsTable = ({
  order,
  isTmsDelivery,
  handlePaymentStart,
  showPaymentButton = true
}: ProfileSaleOrderDetailsTableProps) => {
  const {
    createdAt,
    deliverySlot,
    deliveryAddressStreet: street,
    deliveryAddressName: name,
    deliveryAddressPhoneNumber: phoneNumber,
    deliveryAddressCity: city,
    deliveryAddressPostalCode: postalCode,
    deliveryAddressHouseNumber: houseNumber,
    deliveryAddressFlatNumber: flatNumber,
    requestsInvoice,
    invoiceName,
    invoiceNip,
    invoiceStreet,
    invoiceHouseNumber,
    invoiceFlatNumber,
    invoicePostalCode,
    invoiceCity,
    paymentMethod,
    paymentValue,
    paidAt,
    tmsReservationSlotData
  } = order

  const paymentMethodName = paymentMethod?.name || ''
  const price = paymentValue?.formatted || ''
  const hasPaymentButton = isAwaitingPaymentSaleOrder(order)

  return (
    <div>
      <section className={styles.section}>
        <div className={styles.sectionTitleBar}>
          <h2 className={styles.sectionTitle}>Szczegóły</h2>
        </div>
        <div className={styles.sectionContent}>
          <dl className={styles.sectionItems}>
            <div className={styles.sectionSingleItem}>
              <dt className={styles.sectionItemLabel}>Data zamówienia</dt>
              <dd className={styles.sectionItemValue}>{formatDate(createdAt)}</dd>
            </div>
            <div className={styles.sectionSingleItem}>
              <dt className={styles.sectionItemLabel}>Termin dostawy</dt>
              <dd className={styles.sectionItemValue}>
                {formatDate(deliverySlot?.date || '')}
              </dd>
            </div>
            <div className={styles.sectionSingleItem}>
              <dt className={styles.sectionItemLabel}>Godzina dostawy</dt>
              {isTmsDelivery && tmsReservationSlotData ? (
                <dd className={styles.sectionItemValue}>
                  {formatDeliveryExtendedData(
                    tmsReservationSlotData?.slotExtended?.from || '',
                    tmsReservationSlotData?.slotExtended?.to || ''
                  )}
                </dd>
              ) : (
                <dd className={styles.sectionItemValue}>
                  {deliverySlot?.start} - {deliverySlot?.end}
                </dd>
              )}
            </div>
          </dl>
          {showPaymentButton ? <div className={styles.paymentButtonWrapper} /> : null}
        </div>
      </section>

      <hr className={styles.divider} />

      <section className={styles.section}>
        <div className={styles.sectionTitleBar}>
          <h2 className={styles.sectionTitle}>Adres dostawy</h2>
        </div>
        <div className={styles.sectionContent}>
          <dl className={styles.sectionItems}>
            <div className={styles.sectionSingleItem}>
              <dt className={styles.sectionItemLabel}>Nazwisko i imię</dt>
              <dd className={styles.sectionItemValue}>{name}</dd>
            </div>
            <div className={styles.sectionSingleItem}>
              <dt className={styles.sectionItemLabel}>Numer telefonu</dt>
              <dd className={styles.sectionItemValue}>{phoneNumber}</dd>
            </div>
            <div className={styles.sectionSingleItem}>
              <dt className={styles.sectionItemLabel}>Adres</dt>
              <dd className={styles.sectionItemValue}>
                {`${street} ${houseNumber}${flatNumber ? `/${flatNumber}` : ''}`}
                <br />
                {`${postalCode} ${city}`}
              </dd>
            </div>
          </dl>
          {showPaymentButton ? <div className={styles.paymentButtonWrapper} /> : null}
        </div>
      </section>

      <hr className={styles.divider} />

      {requestsInvoice && (
        <>
          <section className={styles.section}>
            <div className={styles.sectionTitleBar}>
              <h2 className={styles.sectionTitle}>Dane do faktury</h2>
            </div>
            <div className={styles.sectionContent}>
              <dl className={styles.sectionItems}>
                <div className={styles.sectionSingleItem}>
                  <dt className={styles.sectionItemLabel}>Nazwa firmy</dt>
                  <dd className={styles.sectionItemValue}>{invoiceName}</dd>
                </div>
                <div className={styles.sectionSingleItem}>
                  <dt className={styles.sectionItemLabel}>NIP</dt>
                  <dd className={styles.sectionItemValue}>{invoiceNip}</dd>
                </div>
                <div className={styles.sectionSingleItem}>
                  <dt className={styles.sectionItemLabel}>Adres</dt>
                  <dd className={styles.sectionItemValue}>
                    {`${invoiceStreet} ${invoiceHouseNumber}${invoiceFlatNumber ? `/${invoiceFlatNumber}` : ''}`}
                    <br />
                    {`${invoicePostalCode} ${invoiceCity}`}
                  </dd>
                </div>
              </dl>
              {showPaymentButton ? <div className={styles.paymentButtonWrapper} /> : null}
            </div>
          </section>
          <hr className={styles.divider} />
        </>
      )}

      <section className={styles.section}>
        <div className={styles.sectionTitleBar}>
          <h2 className={styles.sectionTitle}>Płatność</h2>
        </div>
        <div className={styles.sectionContent}>
          <dl className={styles.sectionItems}>
            <div className={styles.sectionSingleItem}>
              <dt className={styles.sectionItemLabel}>Metoda płatności</dt>
              <dd className={styles.sectionItemValue}>{paymentMethodName}</dd>
            </div>
            <div className={styles.sectionSingleItem}>
              <dt className={styles.sectionItemLabel}>Data płatności</dt>
              <dd className={styles.sectionItemValue}>
                {paidAt ? formatDate(paidAt) : '---'}
              </dd>
            </div>
            <div className={styles.sectionSingleItem}>
              <dt className={styles.sectionItemLabel}>Kwota</dt>
              <dd className={styles.sectionItemValue} dangerouslySetInnerHTML={{ __html: price }} />
            </div>
          </dl>
          {showPaymentButton ? (
            hasPaymentButton ? (
              <div className={styles.paymentButtonWrapper}>
                <Button
                  variant={ButtonVariants.primary}
                  onClick={handlePaymentStart}
                  startIcon={<IconCreditCard />}
                  size={ButtonSizes.small}
                >
                  Zapłać
                </Button>
              </div>
            ) : (
              <div className={styles.paymentButtonWrapper} />
            )
          ) : null}
        </div>
      </section>
      <hr className={styles.divider} />
    </div>
  )
}

export default ProfileSaleOrderDetailsTable
